/**
 * The Run details card, sheet 20 callout 14 (mission 6 WP08 T048; FR-016,
 * FR-024; GEN.6, ANA.9, D.1; style guide §3, §8).
 *
 * The run's facts as a definition list: labels caption navy-600, values
 * navy-950, and IDs, counts, credits and hashes in mono (CLAUDE.md §5). The run
 * id is shown in full, and the generated file's SHA-256 in full and lowercase
 * (Path A). Times are UTC in `<time dateTime>`, stated once here for the page.
 * A member the run body does not carry is omitted, never printed as empty.
 * NOT DELIVERABLE (GEN.6) shows when `deliverable` is false.
 */
import type { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import type { Run } from '@/lib/api/runs'
import { Mono, count, date, isoDateTime, sha256, time } from '@/lib/format'
import { copyText } from '@/lib/provisional-copy'

const PATH_LABEL: Record<string, string> = { generate: 'Generate', analyze: 'Analyze' }

function When({ iso }: { iso: string }) {
  return (
    <time dateTime={isoDateTime(iso)}>
      <Mono>
        {date(iso)} {time(iso)}
      </Mono>
    </time>
  )
}

export function RunDetailsCard({ run }: { run: Run }) {
  // mission 6 WP13: handoff from WP08: the fact labels come from the register (fact.*).
  const facts: Array<[string, ReactNode]> = [[copyText('fact.runId'), <Mono className="break-all">{run.run_id}</Mono>]]
  if (run.upload) facts.push([copyText('fact.input'), <Mono className="break-all">{run.upload.filename}</Mono>])
  facts.push([copyText('fact.path'), PATH_LABEL[run.path] ?? run.path])
  if (run.layout_version !== undefined) facts.push([copyText('fact.layout'), <Mono>{run.layout_version}</Mono>])
  facts.push([copyText('fact.ruleSet'), <Mono>{run.rule_set_version}</Mono>])
  // owner decision 2026-09-26 (decision 6): a Path B count is base segments.
  facts.push([copyText(run.path === 'analyze' ? 'fact.baseSegments' : 'fact.records'), <Mono>{count(run.record_count)}</Mono>])
  facts.push([
    copyText('fact.credits'),
    <>
      <Mono>{count(run.credits_reserved)}</Mono> {copyText('sublabel.reserved')} · <Mono>{count(run.credits_consumed)}</Mono>{' '}
      {copyText('sublabel.consumed')}
    </>,
  ])
  facts.push([copyText('fact.started'), <When iso={run.started_at} />])
  if (run.completed_at) facts.push([copyText('fact.finished'), <When iso={run.completed_at} />])
  if (run.generated) facts.push(['SHA-256', <Mono className="break-all">{sha256(run.generated.sha256)}</Mono>])

  return (
    <Card padding="compact">
      <CardHeader>
        <CardTitle>Run details</CardTitle>
        {run.deliverable ? null : (
          // mission 7 WP14: J3, the badge text is a register entry (P22).
          <Badge status="not-deliverable" className="self-start">
            {copyText('notDeliverableBadge')}
          </Badge>
        )}
      </CardHeader>
      <dl className="flex flex-col gap-3">
        {facts.map(([label, value]) => (
          <div key={label} className="flex flex-col gap-0.5">
            <dt className="text-caption text-navy-600">{label}</dt>
            <dd className="text-body-sm text-navy-950">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-caption text-navy-600">{copyText('timesInUtc')}</p>
    </Card>
  )
}
